import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FlashMapComponent,
  FlashMapNodeModel,
  FlashMapNodeMetricsMap,
  FlashMapOptions,
  FlashMapDrawingDocument,
  MindMapData,
  FlashMapService,
} from 'flash-map';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  MapMenuAction,
  VersionMenuAction,
} from '../components/map-title-panel/map-title-panel';
import { MapTitlePanelComponent } from '../components/map-title-panel/map-title-panel';
import {
  MindmapAppearanceOffcanvasComponent,
  MindmapLayout,
} from './components/mindmap-appearance-offcanvas/mindmap-appearance-offcanvas';
import {
  MINDMAP_VISUAL_THEMES,
  type MindmapVisualTheme,
} from '../mindmap-theme-palettes';
import {
  MapViewMode,
  MapZoomPanelComponent,
} from '../components/map-zoom-panel/map-zoom-panel';
import {
  MapNoteModalComponent,
  NoteDraftChangedEvent,
  NoteFlushRequestedEvent,
} from '../components/map-note-modal/map-note-modal';
import { MapNodeActionPanelComponent } from '../components/map-node-action-panel/map-node-action-panel';
import { MediaPickerModalComponent } from '@/app/views/modals/media-manager/media-picker-modal';
import { ProcessingLoaderComponent } from '@/app/components/processing-loader/processing-loader';
import { EditorMedia } from '@/app/views/modals/media-manager/media.types';
import { WorkspaceAssetService } from '@core/services/workspace-asset.service';
import { AuthService, PermissionFlag } from '@core/services/auth.service';
import {
  DeckCreatePayload,
  DeckModel,
  DeckService,
} from '@core/services/deck.service';
import { FlashcardDueMetricsService } from '@core/services/flashcard-due-metrics.service';
import {
  MindMapCreatePayload,
  MindMapModel,
  MindMapMasteryModel,
  MindMapRefineNodeWithAiAction,
  MindMapRefineNodeWithAiPayload,
  MindMapService,
  MindMapUpdatePayload,
} from '@core/services/mindmap.service';
import {
  Subject,
  Observable,
  EMPTY,
  catchError,
  concatMap,
  debounceTime,
  defer,
  finalize,
  firstValueFrom,
  from,
  map,
  of,
  retry,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnnotationService } from '@core/services/annotation.service';
import {
  AnnotationLinkModel,
  AnnotationLinkService,
} from '@core/services/annotation-link.service';
import { ShareSettingModal } from '@/app/views/modals/share-setting-modal/share-setting-modal';
import { InviteSettingModal } from '@/app/views/modals/invite-setting-modal/invite-setting-modal';
import { RestoreVersionConfirmModal } from '@/app/views/modals/restore-version-confirm-modal/restore-version-confirm-modal';
import { DeleteConfirmModal } from '@/app/views/modals/delete-confirm-modal/delete-confirm-modal';
import { DeckAssociationModal } from '@/app/views/mindmap/components/deck-association-modal/deck-association-modal';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { UiLocaleService } from '@core/services/ui-locale.service';
import { AiContentLanguageService } from '@core/services/ai-content-language.service';
import { LayoutService } from '@core/services/layout-store.service';
import { MindmapTranslations } from '../mindmap-translations';
import {
  MindMapVersionModel,
  MindMapVersionService,
} from '@core/services/mindmap-version.service';
import {
  TrainingModeModal,
  TrainingModeModalOption,
  TrainingModeModalResult,
} from '@/app/views/modals/training-mode-modal/training-mode-modal';
import { MarkdownParser } from '@core/utils/markdown-parser';
import { toNonNegativeMetric } from '@core/utils/flashcard-review-metrics.util';
import {
  AccountPlanLimitService,
  isMaxDecksLimitError,
  isMaxMindMapsAttachmentsLimitError,
  isMaxMindMapsLimitError,
  isMaxAiCreditsMonthLimitError,
} from '@core/services/account-plan-limit.service';
import { ApiError } from '@core/services/client.service';
import { AiCreditsPurchaseService } from '@core/services/ai-credits-purchase.service';
import { PlanLimitNotificationService } from '@core/services/plan-limit-notification.service';

type MindMapTreeNode = {
  id: string;
  topic: string;
  expanded?: boolean;
  image?: MindMapNodeImage;
  children?: MindMapTreeNode[];
  [key: string]: unknown;
};

type MindMapNodeImage = {
  src?: string;
  workspaceAssetId?: number;
  position?: 'top' | 'right' | 'bottom' | 'left';
  width?: number;
  height?: number;
};

type MindMapAppearance = {
  layout: MindmapLayout;
  theme: MindmapVisualTheme;
};

type StoredMindMapData = MindMapData & {
  appearance?: Partial<MindMapAppearance>;
};

type FlashMapEditEvent = {
  evt?: string;
  data?: unknown[];
  node?: string;
  nodes?: string[];
};

type MindMapSaveRequest = {
  flush: boolean;
  onDone?: () => void;
  onFailed?: () => void;
};

const MAX_PATH_NODES = 4;
const NOTE_SYNC_DEBOUNCE_MS = 1200;
const MAX_EXPAND_CONTENT_NEW_NODES = 5;
const MAX_EXPAND_CONTENT_DEPTH = 2;

const DEFAULT_MINDMAP_APPEARANCE: MindMapAppearance = {
  layout: 'mindmap',
  theme: 'colorful',
};

const MINDMAP_LAYOUTS: readonly MindmapLayout[] = [
  'mindmap',
  'org-chart',
  'list',
];

type MindMapLoadState = 'idle' | 'loading' | 'ready' | 'error';

type ExpandContentNodeInput = {
  topic?: unknown;
  children?: unknown;
};

type ExpandContentTargetItemInput = {
  targetNodeId?: unknown;
  targetTopic?: unknown;
  nodes?: unknown;
  children?: unknown;
};

type ExpandContentNodeDraft = {
  topic: string;
  children?: ExpandContentNodeDraft[];
};

type ExpandContentNodeForInsert = {
  id: string;
  topic: string;
  children?: ExpandContentNodeForInsert[];
};

type ParsedExpandContentResult = {
  targetItems: ExpandContentTargetItemInput[];
  legacyNodes: ExpandContentNodeInput[] | null;
};

type AnnotationLinkCacheEntry = {
  annotationId: number;
  linkId: number | null;
};

type FlashcardDeckClickEvent = {
  nodeId: string;
};

type DeckAssociationClickEvent = {
  nodeId: string;
};

type FlashcardTrainingClickEvent = {
  nodeId: string;
};

type RefineNodeClickEvent = {
  nodeId: string;
  action: MindMapRefineNodeWithAiAction;
};

const applyDepthCollapse = (
  node: MindMapTreeNode,
  maxPathNodes: number,
  depth: number = 0,
): void => {
  if (!Array.isArray(node.children)) {
    return;
  }

  if (depth >= maxPathNodes - 1 && typeof node.expanded === 'undefined') {
    node.expanded = false;
  }

  for (const child of node.children) {
    applyDepthCollapse(child, maxPathNodes, depth + 1);
  }
};

@Component({
  selector: 'app-mindmap-edit',
  imports: [
    FlashMapComponent,
    MapTitlePanelComponent,
    MapZoomPanelComponent,
    MapNoteModalComponent,
    MapNodeActionPanelComponent,
    ProcessingLoaderComponent,
  ],
  templateUrl: './mindmap-edit.html',
  styleUrls: ['./mindmap-edit.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MindmapEdit implements OnInit {
  @ViewChild('flashMap') flashMap?: FlashMapComponent;

  readonly options = signal<Partial<FlashMapOptions>>({
    layout_mode: 'mindmap',
    layout: {
      mindmap_hspace: 50,
    },
    default_event_handle: {
      enable_dblclick_handle: false,
    },
    view: {
      draggable: true,
      hide_scrollbars_when_draggable: true,
      pinch_sensitivity: 3.45,
      wheel_sensitivity: 3.85,
      zoom: {
        min: 0.2,
        max: 9,
        step: 0.1,
        mask_key: 0,
      },
    },
    shortcut: {
      mapping: {
        addchild: [9, 45, 4096 + 13], // Tab, Insert or Ctrl+Enter
      },
    },
    drawing: {
      enabled: true,
    },
    support_html: true,
  });

  readonly mindData = signal<MindMapData | null>(null);
  readonly drawingDocument = signal<FlashMapDrawingDocument>({
    version: 1,
    strokes: [],
  });
  readonly drawingToolsVisible = signal(false);
  readonly viewMode = signal<MapViewMode>('mindmap');
  private readonly masterySummary = signal<MindMapMasteryModel | null>(null);
  readonly nodeMetrics = computed<FlashMapNodeMetricsMap>(() => {
    // Keep this explicit dependency so metric labels are immediately rebuilt
    // when the UI locale changes, before the translation catalog finishes loading.
    this.uiLocaleService.locale();

    const summary = this.masterySummary();
    if (!summary) {
      return {};
    }

    const metrics: FlashMapNodeMetricsMap = {};
    for (const totals of summary.nodes ?? []) {
      const metric = this.buildNodeMetric(totals);
      if (metric) {
        metrics[totals.nodeKey] = {
          ...metric,
          tooltip:
            totals.flashcards > 0
              ? {
                  totalCards: totals.flashcards,
                  studiedCards: totals.studiedCards,
                  matureCards: totals.matureCards,
                  ...this.buildNodeMetricTooltipText(totals),
                }
              : null,
        };
      }
    }

    delete metrics['root'];
    const rootMetric = this.buildRootMetric(summary);
    if (rootMetric) {
      metrics['root'] = rootMetric;
    }

    return metrics;
  });
  readonly mindMapLoadState = signal<MindMapLoadState>('idle');
  readonly mindMapLoadError = signal<string | null>(null);
  readonly isRefiningNodeWithAi = signal(false);
  readonly shouldShowNodeActionPanel = computed(
    () => !this.isRefiningNodeWithAi() && this.mindMapLoadState() !== 'loading',
  );
  private readonly updateRequests = new Subject<void>();
  private readonly saveRequests = new Subject<MindMapSaveRequest>();
  private readonly noteSyncDebounceRequests = new Subject<void>();
  private readonly noteSyncQueue = new Subject<string | null>();
  private readonly pendingNoteDrafts = new Map<string, string>();
  private readonly annotationLinkCache = new Map<
    string,
    AnnotationLinkCacheEntry
  >();

  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutStore = inject(LayoutService);
  private readonly uiLocaleService = inject(UiLocaleService);
  private readonly aiContentLanguageService = inject(AiContentLanguageService);
  private readonly uiTranslation = inject(UiTranslationService);
  private readonly accountPlanLimitService = inject(AccountPlanLimitService);
  private readonly planLimitNotificationService = inject(
    PlanLimitNotificationService,
  );
  private readonly aiCreditsPurchaseService = inject(AiCreditsPurchaseService);
  protected readonly i18n =
    this.uiTranslation.namespace<MindmapTranslations>('mindmap');
  private readonly deckI18n = this.uiTranslation.namespace('deck');
  readonly flashMapLanguage = computed(() => {
    switch (this.uiLocaleService.locale()) {
      case 'pt-BR':
        return 'pt';
      case 'es-ES':
        return 'es';
      default:
        return 'en';
    }
  });
  readonly flashMapAppearanceTheme = computed(
    () => this.layoutStore.state().theme,
  );
  readonly flashMapVisualTheme = signal<MindmapVisualTheme>('colorful');
  readonly flashMapLayout = signal<MindmapLayout>('mindmap');
  readonly mindMapNotesBlocked = computed(() =>
    this.accountPlanLimitService.mindMapNotesBlocked(),
  );
  readonly mindMapExportBlocked = computed(() =>
    this.accountPlanLimitService.mindMapExportBlocked(),
  );
  readonly mindMapShareBlocked = computed(() =>
    this.accountPlanLimitService.mindMapShareBlocked(),
  );
  readonly mindMapInviteBlocked = computed(() =>
    this.accountPlanLimitService.mindMapInviteBlocked(),
  );
  currentMindMap: MindMapModel | null = null;
  private shouldFocusRoot = false;
  private isCreating = false;
  private isDuplicating = false;
  private isDeletingMindMap = false;
  private restoringVersionId: number | null = null;
  private isRestoringVersion = false;
  protected requestedMindMapId: number | null = null;
  private mindMapLoadRequestId = 0;
  private mindMapDataApplyRequestId = 0;
  private mindMapChangeRevision = 0;
  private persistedMindMapChangeRevision = 0;
  private readonly pendingMindMapSaveCallbacks: Array<() => void> = [];
  private readonly mindMapImageObjectUrls = new Set<string>();

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly mindMapService = inject(MindMapService);
  private readonly deckService = inject(DeckService);
  private readonly dueMetricsService = inject(FlashcardDueMetricsService);
  private readonly modalService = inject(NgbModal);
  private readonly offcanvas = inject(NgbOffcanvas);
  private readonly toastr = inject(ToastrService);
  private readonly authService = inject(AuthService);
  private readonly annotationService = inject(AnnotationService);
  private readonly annotationLinkService = inject(AnnotationLinkService);
  private readonly mindMapVersionService = inject(MindMapVersionService);
  private readonly workspaceAssetService = inject(WorkspaceAssetService);

  readonly canCreateMindMap = this.authService.hasPermission(
    'Mindmap',
    PermissionFlag.Create,
  );
  readonly canUpdateMindMap = this.authService.hasPermission(
    'Mindmap',
    PermissionFlag.Update,
  );
  readonly canDeleteMindMap = this.authService.hasPermission(
    'Mindmap',
    PermissionFlag.Delete,
  );

  get versionMindMapId(): number | null {
    return this.currentMindMap?.id ?? null;
  }

  get versionWorkspaceId(): number | null {
    return (
      this.currentMindMap?.workspaceId ??
      this.authService.GetWorkspace() ??
      null
    );
  }

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      this.mindMapDataApplyRequestId += 1;
      this.revokeMindMapImageObjectUrls();
    });
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const idParam = params.get('id');
        this.shouldFocusRoot = params.get('new') === '1';

        this.debugMindMapRender('route parameters received', {
          idParam,
          shouldFocusRoot: this.shouldFocusRoot,
          currentMindMapId: this.currentMindMap?.id ?? null,
          hasMindData: this.mindData() !== null,
        });

        if (!idParam) {
          this.masterySummary.set(null);
          if (this.shouldFocusRoot) {
            this.resetMindMapLoadState();
            this.createMindMapFromContext();
            return;
          }

          this.showMindMapLoadError(this.i18n.tr('invalidMindMapId'));
          return;
        }

        const id = this.parseMindMapId(idParam);
        if (!id) {
          this.masterySummary.set(null);
          this.showMindMapLoadError(this.i18n.tr('invalidMindMapId'));
          return;
        }

        const isHydratedNewMindMap =
          this.shouldFocusRoot &&
          this.currentMindMap?.id === id &&
          this.mindData() !== null;

        if (isHydratedNewMindMap) {
          // Avoid a second show/reset cycle after create+navigate, which can race editor commit and drop focus.
          this.debugMindMapRender('skipping reload for hydrated new mind map', {
            id,
          });
          this.scheduleRootFocusRetry();
          return;
        }

        this.loadMindMap(id);
      });

    this.updateRequests
      .pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveRequests.next({ flush: false }));

    this.saveRequests
      .pipe(
        concatMap((request) =>
          (request.flush
            ? this.persistLatestPendingMindMapUpdate()
            : this.persistPendingMindMapUpdate()
          ).pipe(
            tap((saved) => {
              if (!saved) {
                request.onFailed?.();
                if (request.flush && request.onDone) {
                  if (!request.onFailed) {
                    this.pendingMindMapSaveCallbacks.push(request.onDone);
                  }
                }
                return;
              }

              request.onDone?.();
              this.flushPendingMindMapSaveCallbacks();
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.noteSyncDebounceRequests
      .pipe(
        debounceTime(NOTE_SYNC_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.noteSyncQueue.next(null);
      });

    this.noteSyncQueue
      .pipe(
        concatMap((nodeId) => from(this.syncPendingNoteDrafts(nodeId))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onDrawingToolsToggle(visible: boolean): void {
    this.drawingToolsVisible.set(visible);
    this.flashMap?.drawingPanelVisible.set(visible);
  }

  onZoomModeChange(mode: MapViewMode): void {
    this.viewMode.set(mode);

    if (mode === 'focus') {
      this.drawingToolsVisible.set(false);
      this.flashMap?.drawingPanelVisible.set(false);
    }
  }

  onMenuAction(action: MapMenuAction): void {
    switch (action) {
      case 'back':
        if (this.canUpdateMindMap) {
          this.persistMindMapThen(() => this.navigateBackToOrigin());
        } else {
          this.navigateBackToOrigin();
        }
        break;
      case 'move-trash':
        if (this.canDeleteMindMap) this.confirmMoveMindMapToTrash();
        break;
      case 'duplicate':
        if (this.canCreateMindMap) this.duplicateMindMap();
        break;
      case 'share':
        if (!this.canUpdateMindMap) break;
        if (this.mindMapShareBlocked()) {
          this.showMindMapShareLimitReached();
          break;
        }
        this.openShareModal();
        break;
      case 'invite':
        if (!this.canUpdateMindMap) break;
        if (this.mindMapInviteBlocked()) {
          this.showMindMapInviteLimitReached();
          break;
        }
        this.openInviteModal();
        break;
      case 'decks':
        if (this.currentMindMap?.id) {
          const mindMapId = this.currentMindMap.id;
          this.persistMindMapThen(() => {
            this.router.navigate(['/deck'], {
              queryParams: {
                mindMapId,
                from: 'mindmap-edit',
              },
            });
          });
        }
        break;
      case 'appearance':
        if (this.canUpdateMindMap) this.openAppearanceOffcanvas();
        break;
      case 'versions':
        // Nova lÃ³gica serÃ¡ implementada no menu
        break;
      default:
        break;
    }
  }

  private confirmMoveMindMapToTrash(): void {
    if (this.isDeletingMindMap) {
      return;
    }

    const mindMap = this.currentMindMap;
    if (!mindMap?.id) {
      this.toastr.error(this.i18n.tr('mapContextUnavailable'));
      return;
    }

    const modalRef = this.modalService.open(DeleteConfirmModal, {
      centered: true,
      backdrop: 'static',
      windowClass: 'modal-alert modal-delete-alert',
    });
    modalRef.componentInstance.itemName = mindMap.title;
    modalRef.componentInstance.itemTypeLabel = this.i18n.tr('itemTypeMindmap');

    modalRef.closed
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (!confirmed || this.isDeletingMindMap) {
          return;
        }

        this.isDeletingMindMap = true;
        this.persistMindMapThen(
          () => this.deleteMindMap(mindMap.id),
          () => {
            this.isDeletingMindMap = false;
          },
        );
      });
  }

  private deleteMindMap(mindMapId: number): void {
    this.mindMapService
      .delete(mindMapId)
      .pipe(
        finalize(() => {
          this.isDeletingMindMap = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.router.navigate(['/trash']);
        },
        error: (error: unknown) => {
          const message =
            error instanceof Error && error.message
              ? error.message
              : this.i18n.tr('errorDeleteItem', {
                  itemType: this.i18n.tr('itemTypeMindmap'),
                });
          this.toastr.error(message);
        },
      });
  }

  private openAppearanceOffcanvas(): void {
    const offcanvasRef = this.offcanvas.open(
      MindmapAppearanceOffcanvasComponent,
      {
        position: 'start',
        backdrop: 'static',
        keyboard: false,
        animation: true,
        scroll: false,
        panelClass: 'app-drawer mindmap-appearance-drawer',
      },
    );

    offcanvasRef.componentInstance.setInitialLayout(this.flashMapLayout());
    offcanvasRef.componentInstance.setInitialTheme(this.flashMapVisualTheme());
    offcanvasRef.componentInstance.themeChange.subscribe(
      (theme: MindmapVisualTheme) => {
        this.getFlashMapService()?.clearNodeCustomStyles();
        this.flashMapVisualTheme.set(theme);
        this.requestMindMapAutosave();
      },
    );
    offcanvasRef.componentInstance.layoutChange.subscribe(
      (layout: MindmapLayout) => {
        this.setFlashMapLayout(layout);
        this.requestMindMapAutosave();
      },
    );
  }

  private setFlashMapLayout(layout: MindmapLayout): void {
    this.flashMapLayout.set(layout);
    this.options.update((options) => ({ ...options, layout_mode: layout }));
    this.getFlashMapService()?.setLayoutMode?.(layout);
  }
  handleVersionRowClick(version: MindMapVersionModel): void {
    this.mindMapVersionService
      .get(version.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (versionDetail) => {
          if (!versionDetail.data) {
            this.toastr.error(this.i18n.tr('errorLoadingMindMapVersion'));
            return;
          }

          void this.applyMindMapData(versionDetail.data).then((applied) => {
            if (!applied) {
              return;
            }

            this.toastr.info(this.i18n.tr('mindMapVersionOpened'));
          });
        },
        error: () => {
          this.toastr.error(this.i18n.tr('errorLoadingMindMapVersion'));
        },
      });
  }

  handleVersionMenuAction(event: {
    action: VersionMenuAction;
    version: MindMapVersionModel;
  }): void {
    if (event.action === 'restore') {
      if (this.canUpdateMindMap) this.restoreMindMapVersion(event.version);
      return;
    }

    if (event.action === 'copy') {
      if (this.canCreateMindMap) this.copyMindMapVersion(event.version);
    }
  }

  public handleFlashcardDeckClick(event: FlashcardDeckClickEvent): void {
    const nodeKey = event.nodeId?.trim() ?? '';
    if (!nodeKey) {
      return;
    }

    this.resolveOrCreateDeckFromNode(nodeKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (deck) => {
          const mindMapId = this.currentMindMap?.id ?? null;
          if (deck && deck.id > 0 && mindMapId) {
            this.persistMindMapThen(() => {
              this.router.navigate(['/deck-edit', deck.id], {
                queryParams: { mindMapId, from: 'mindmap-edit' },
              });
            });
          }
        },
        error: (error: unknown) => {
          console.error(this.i18n.tr('errorOpenOrCreateDeckFromNode'), error);
        },
      });
  }

  public handleDeckAssociationClick(event: DeckAssociationClickEvent): void {
    const nodeKey = event.nodeId?.trim() ?? '';
    const mindMapId = this.currentMindMap?.id ?? null;
    const workspaceId = this.authService.GetWorkspace();
    const selectedNode = this.getFlashMapService()?.getNode(nodeKey);
    if (!nodeKey || !mindMapId || !workspaceId || selectedNode?.isroot) {
      return;
    }

    const modalRef = this.modalService.open(DeckAssociationModal, {
      size: 'lg',
      backdrop: 'static',
      windowClass: 'deck-modal-window',
    });
    modalRef.componentInstance.workspaceId = workspaceId;
    modalRef.componentInstance.mindMapId = mindMapId;
    modalRef.componentInstance.nodeKey = nodeKey;
    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((deck: DeckModel | undefined) => {
        if (deck?.id) {
          this.loadNodeMetrics(mindMapId);
        }
      });
  }

  public handleFlashcardTrainingClick(
    event: FlashcardTrainingClickEvent,
  ): void {
    const nodeKey = event.nodeId?.trim() ?? '';
    if (!nodeKey) {
      return;
    }

    this.resolveOrCreateDeckFromNode(nodeKey)
      .pipe(
        switchMap((deck) => {
          if (!deck || deck.id <= 0) {
            return of(null);
          }

          const totalCards = toNonNegativeMetric(deck.flashcardCount);
          if (totalCards <= 0) {
            this.openTrainingModeModal(deck, 0);
            return of(null);
          }

          return this.dueMetricsService.countDueCardsByDeck(deck.id).pipe(
            tap((dueCards) => this.openTrainingModeModal(deck, dueCards)),
            catchError(() => {
              this.openTrainingModeModal(deck, null);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: (error: unknown) => {
          console.error(this.i18n.tr('errorOpenOrCreateDeckFromNode'), error);
        },
      });
  }

  public handleRefineNodeClick(event: RefineNodeClickEvent): void {
    if (!this.canUpdateMindMap) {
      return;
    }
    if (this.isRefiningNodeWithAi()) {
      return;
    }

    if (event.action === 'add-notes' && this.mindMapNotesBlocked()) {
      this.showMindMapNotesLimitReached();
      return;
    }

    const nodeId = event.nodeId?.trim() ?? '';
    if (!nodeId) {
      return;
    }

    const currentMindMapId = this.currentMindMap?.id ?? null;
    const service = this.getFlashMapService();
    const node = service?.getNode(nodeId);
    const currentTopic = node?.topic?.trim() ?? '';
    if (!currentMindMapId || !service || !currentTopic) {
      this.toastr.error(this.i18n.tr('mapContextUnavailable'));
      return;
    }

    this.isRefiningNodeWithAi.set(true);
    this.accountPlanLimitService
      .canUseAiCreditsFresh()
      .pipe(
        take(1),
        switchMap((canUseAiCredits) => {
          if (!canUseAiCredits) {
            this.aiCreditsPurchaseService.showLimitReached(
              this.deckI18n.tr('common.aiCreditsLimitReached'),
            );
            return EMPTY;
          }

          return from(
            this.buildRefinePayload(currentMindMapId, nodeId, event.action),
          ).pipe(
            switchMap((payload) =>
              this.mindMapService.refineNodeWithAi(payload),
            ),
          );
        }),
        finalize(() => this.isRefiningNodeWithAi.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          const resultText = result.resultText?.trim() ?? '';
          if (!resultText) {
            this.toastr.error('Unable to refine this node.');
            return;
          }

          if (event.action === 'add-notes') {
            this.applyRefineNote(
              nodeId,
              this.formatRefinedNoteForEditor(resultText),
            );
            return;
          }

          if (event.action === 'expand-content') {
            const inserted = this.applyExpandContentResult(nodeId, resultText);
            if (!inserted) {
              this.toastr.error('Unable to refine this node.');
              return;
            }

            this.requestMindMapAutosave();
            this.toastr.success('Node refined successfully.');
            return;
          }

          service.updateNode(nodeId, resultText);
          this.requestMindMapAutosave();
          this.toastr.success('Node refined successfully.');
        },
        error: (error: unknown) => {
          if (isMaxAiCreditsMonthLimitError(error)) {
            this.accountPlanLimitService.refresh().subscribe();
            this.aiCreditsPurchaseService.showLimitReached(
              this.deckI18n.tr('common.aiCreditsLimitReached'),
            );
            return;
          }

          console.error('MindMap refine failed', error);
          this.toastr.error('Unable to refine this node.');
        },
      });
  }

  private resolveOrCreateDeckFromNode(
    nodeKey: string,
  ): Observable<DeckModel | null> {
    const mindMapId = this.currentMindMap?.id ?? null;
    const workspaceId = this.authService.GetWorkspace();
    const selectedNode = this.getFlashMapService()?.getNode(nodeKey);
    if (!nodeKey || !mindMapId || !workspaceId || selectedNode?.isroot) {
      return of(null);
    }

    const createPayload = this.buildDeckCreatePayload(
      workspaceId,
      mindMapId,
      nodeKey,
    );

    return this.deckService.getOrCreateMindMapNode(createPayload).pipe(
      tap(() => this.refreshAccountPlanLimits()),
      catchError((error: unknown) => {
        if (isMaxDecksLimitError(error)) {
          this.showDeckLimitReached();
          this.refreshAccountPlanLimits();
          return of(null);
        }

        throw error;
      }),
    );
  }

  private async buildRefinePayload(
    mindMapId: number,
    nodeId: string,
    action: MindMapRefineNodeWithAiAction,
  ): Promise<MindMapRefineNodeWithAiPayload> {
    const service = this.getFlashMapService();
    const node = service?.getNode(nodeId);
    const currentTopic = node?.topic?.trim() ?? '';
    const childrenTopics = (node?.children ?? [])
      .map((child) => child.topic?.trim() ?? '')
      .filter((value) => value.length > 0);

    const payload: MindMapRefineNodeWithAiPayload = {
      mindMapId,
      nodeId,
      action,
      currentTopic,
      nodePath: this.resolveNodePath(nodeId) ?? currentTopic,
      nodeContextTree: this.resolveNodeContextTree(nodeId) ?? undefined,
      childrenTopics,
      contentLanguageCode: await this.resolveContentLanguageCode(),
    };

    if (action !== 'expand-content') {
      payload.currentNote = await this.resolveCurrentNodeNote(nodeId);
    }

    return payload;
  }

  private async resolveContentLanguageCode(): Promise<string | null> {
    return firstValueFrom(this.aiContentLanguageService.resolve());
  }

  private resolveNodePath(nodeId: string): string | null {
    const data = this.mindData()?.data as MindMapTreeNode | null;
    if (!data || typeof data !== 'object') {
      return null;
    }

    const path = this.findNodePath(data, nodeId, []);
    return path.length > 0 ? path.join(' > ') : null;
  }

  private resolveNodeContextTree(nodeId: string): string | null {
    const data = this.mindData()?.data as MindMapTreeNode | null;
    if (!data || typeof data !== 'object') {
      return null;
    }

    const trail = this.findNodeTrail(data, nodeId, []);
    if (trail.length === 0) {
      return null;
    }

    const selectedNode = trail[trail.length - 1];
    const pathLines = trail
      .map((entry, index) => this.formatNodeContextLine(entry, index))
      .filter((line): line is string => line !== null)
      .join('\n');
    const childrenLines =
      this.serializeNodeContextSubtree(selectedNode).join('\n');

    return `Path root to selected:\n${pathLines}\nSelected node children:\n${childrenLines}`;
  }

  private serializeNodeContextSubtree(
    node: MindMapTreeNode,
    depth: number = 0,
  ): string[] {
    const lines: string[] = [];
    const topic = typeof node.topic === 'string' ? node.topic.trim() : '';
    const children = Array.isArray(node.children) ? node.children : [];

    let childrenDepth = depth;
    if (topic) {
      const line = this.formatNodeContextLine(node, depth);
      if (line) {
        lines.push(line);
      }
      childrenDepth = depth + 1;
    }

    for (const child of children) {
      lines.push(...this.serializeNodeContextSubtree(child, childrenDepth));
    }

    return lines;
  }

  private formatNodeContextLine(
    node: MindMapTreeNode,
    depth: number,
  ): string | null {
    const topic = typeof node.topic === 'string' ? node.topic.trim() : '';
    if (!topic) {
      return null;
    }

    const nodeId = typeof node.id === 'string' ? node.id.trim() : '';
    const prefix = '  '.repeat(depth);
    if (!nodeId) {
      return `${prefix}- ${topic}`;
    }

    return `${prefix}- [${nodeId}] ${topic}`;
  }

  private findNodePath(
    node: MindMapTreeNode,
    targetNodeId: string,
    ancestors: string[],
  ): string[] {
    const topic = typeof node.topic === 'string' ? node.topic.trim() : '';
    const nextAncestors = topic ? [...ancestors, topic] : ancestors;
    if (node.id === targetNodeId) {
      return nextAncestors;
    }

    const children = Array.isArray(node.children) ? node.children : [];
    for (const child of children) {
      const found = this.findNodePath(child, targetNodeId, nextAncestors);
      if (found.length > 0) {
        return found;
      }
    }

    return [];
  }

  private findNodeTrail(
    node: MindMapTreeNode,
    targetNodeId: string,
    ancestors: MindMapTreeNode[],
  ): MindMapTreeNode[] {
    const topic = typeof node.topic === 'string' ? node.topic.trim() : '';
    const nextAncestors = topic ? [...ancestors, node] : ancestors;
    if (node.id === targetNodeId) {
      return nextAncestors;
    }

    const children = Array.isArray(node.children) ? node.children : [];
    for (const child of children) {
      const found = this.findNodeTrail(child, targetNodeId, nextAncestors);
      if (found.length > 0) {
        return found;
      }
    }

    return [];
  }

  private applyExpandContentResult(
    nodeId: string,
    resultText: string,
  ): boolean {
    const service = this.getFlashMapService();
    const selectedNode = service?.getNode(nodeId);
    if (!service || !selectedNode) {
      return false;
    }

    const parsed = this.parseExpandContentNodes(resultText);
    if (!parsed) {
      return false;
    }

    let remaining = MAX_EXPAND_CONTENT_NEW_NODES;
    let insertedAny = false;

    if (parsed.targetItems.length > 0) {
      const subtreeNodes = this.collectSubtreeNodes(selectedNode);
      const nodeById = new Map<string, FlashMapNodeModel>();
      const firstNodeByTopic = new Map<string, FlashMapNodeModel>();
      for (const node of subtreeNodes) {
        const normalizedId = node.id?.trim();
        if (normalizedId) {
          nodeById.set(normalizedId, node);
        }

        const normalizedTopic = node.topic?.trim().toLowerCase();
        if (normalizedTopic && !firstNodeByTopic.has(normalizedTopic)) {
          firstNodeByTopic.set(normalizedTopic, node);
        }
      }

      for (const item of parsed.targetItems) {
        if (remaining <= 0) {
          break;
        }

        const targetNode = this.resolveExpandContentTargetNode(
          item,
          nodeById,
          firstNodeByTopic,
        );
        if (!targetNode) {
          continue;
        }

        const nodesInput = this.resolveExpandContentNodesArray(item);
        if (!nodesInput || nodesInput.length === 0) {
          continue;
        }

        const normalized = this.normalizeExpandContentNodes(
          nodesInput,
          targetNode,
          remaining,
        );
        if (normalized.length === 0) {
          continue;
        }

        const inserted = service.addNodes(targetNode.id, normalized);
        if (inserted.length > 0) {
          insertedAny = true;
          remaining -= this.countExpandContentNodes(normalized);
        }
      }
    }

    if (insertedAny) {
      return true;
    }

    if (
      !parsed.legacyNodes ||
      parsed.legacyNodes.length === 0 ||
      remaining <= 0
    ) {
      return false;
    }

    const normalizedLegacy = this.normalizeExpandContentNodes(
      parsed.legacyNodes,
      selectedNode,
      remaining,
    );
    if (normalizedLegacy.length === 0) {
      return false;
    }

    const insertedLegacy = service.addNodes(nodeId, normalizedLegacy);
    return insertedLegacy.length > 0;
  }

  private parseExpandContentNodes(
    resultText: string,
  ): ParsedExpandContentResult | null {
    const normalized = resultText.replace(/\r\n?/g, '\n').trim();
    const fromFenceMatch = normalized.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    const candidate = (fromFenceMatch?.[1] ?? normalized).trim();

    let parsed: unknown;
    try {
      parsed = JSON.parse(candidate);
    } catch {
      return null;
    }

    if (Array.isArray(parsed)) {
      return {
        targetItems: [],
        legacyNodes: parsed as ExpandContentNodeInput[],
      };
    }

    if (parsed && typeof parsed === 'object') {
      const record = parsed as Record<string, unknown>;
      const targetItems = Array.isArray(record['items'])
        ? (record['items'] as ExpandContentTargetItemInput[])
        : [];

      if (Array.isArray(record['nodes'])) {
        return {
          targetItems,
          legacyNodes: record['nodes'] as ExpandContentNodeInput[],
        };
      }
      if (Array.isArray(record['children'])) {
        return {
          targetItems,
          legacyNodes: record['children'] as ExpandContentNodeInput[],
        };
      }

      return {
        targetItems,
        legacyNodes: null,
      };
    }

    return null;
  }

  private resolveExpandContentNodesArray(
    item: ExpandContentTargetItemInput,
  ): ExpandContentNodeInput[] | null {
    if (Array.isArray(item.nodes)) {
      return item.nodes as ExpandContentNodeInput[];
    }

    if (Array.isArray(item.children)) {
      return item.children as ExpandContentNodeInput[];
    }

    return null;
  }

  private collectSubtreeNodes(root: FlashMapNodeModel): FlashMapNodeModel[] {
    const result: FlashMapNodeModel[] = [];
    const visit = (node: FlashMapNodeModel): void => {
      result.push(node);
      const children = Array.isArray(node.children) ? node.children : [];
      for (const child of children) {
        visit(child);
      }
    };

    visit(root);
    return result;
  }

  private resolveExpandContentTargetNode(
    item: ExpandContentTargetItemInput,
    nodeById: Map<string, FlashMapNodeModel>,
    firstNodeByTopic: Map<string, FlashMapNodeModel>,
  ): FlashMapNodeModel | null {
    const targetNodeId =
      typeof item.targetNodeId === 'string' ? item.targetNodeId.trim() : '';
    if (targetNodeId) {
      const byId = nodeById.get(targetNodeId);
      if (byId) {
        return byId;
      }
    }

    const targetTopic =
      typeof item.targetTopic === 'string'
        ? item.targetTopic.trim().toLowerCase()
        : '';
    if (!targetTopic) {
      return null;
    }

    return firstNodeByTopic.get(targetTopic) ?? null;
  }

  private normalizeExpandContentNodes(
    nodes: ExpandContentNodeInput[],
    targetNode: FlashMapNodeModel,
    remaining: number,
  ): ExpandContentNodeForInsert[] {
    const targetChildrenTopics = new Set(
      (targetNode.children ?? [])
        .map(
          (child: FlashMapNodeModel) => child.topic?.trim().toLowerCase() ?? '',
        )
        .filter((topic: string) => topic.length > 0),
    );
    const draftNodes = this.takeNormalizedExpandNodes(
      nodes,
      targetChildrenTopics,
      1,
      remaining,
    );
    return draftNodes.map((draft) => this.toInsertNode(draft));
  }

  private takeNormalizedExpandNodes(
    nodes: ExpandContentNodeInput[],
    existingTopics: Set<string>,
    depth: number,
    remaining: number,
  ): ExpandContentNodeDraft[] {
    if (remaining <= 0 || depth > MAX_EXPAND_CONTENT_DEPTH) {
      return [];
    }

    const normalized: ExpandContentNodeDraft[] = [];
    const seenTopics = new Set<string>();
    let available = remaining;

    for (const item of nodes) {
      if (available <= 0) {
        break;
      }

      const topic = typeof item?.topic === 'string' ? item.topic.trim() : '';
      if (!topic) {
        continue;
      }

      const normalizedTopic = topic.toLowerCase();
      if (
        seenTopics.has(normalizedTopic) ||
        existingTopics.has(normalizedTopic)
      ) {
        continue;
      }

      seenTopics.add(normalizedTopic);
      const draft: ExpandContentNodeDraft = { topic };
      available -= 1;

      if (depth < MAX_EXPAND_CONTENT_DEPTH && available > 0) {
        const rawChildren = Array.isArray(item.children)
          ? (item.children as ExpandContentNodeInput[])
          : [];
        const nested = this.takeNormalizedExpandNodes(
          rawChildren,
          new Set<string>(),
          depth + 1,
          available,
        );
        if (nested.length > 0) {
          draft.children = nested;
          available -= nested.length;
        }
      }

      normalized.push(draft);
    }

    return normalized;
  }

  private toInsertNode(
    node: ExpandContentNodeDraft,
  ): ExpandContentNodeForInsert {
    const children =
      node.children && node.children.length > 0
        ? node.children.map((child) => this.toInsertNode(child))
        : undefined;

    return {
      id: this.generateMindMapNodeId(),
      topic: node.topic,
      ...(children ? { children } : {}),
    };
  }

  private generateMindMapNodeId(): string {
    const randomPart = Math.random().toString(36).slice(2, 10);
    return `node_${Date.now()}_${randomPart}`;
  }

  private countExpandContentNodes(nodes: ExpandContentNodeForInsert[]): number {
    let total = 0;
    for (const node of nodes) {
      total += 1;
      if (Array.isArray(node.children) && node.children.length > 0) {
        total += this.countExpandContentNodes(node.children);
      }
    }
    return total;
  }

  private async resolveCurrentNodeNote(nodeId: string): Promise<string | null> {
    const targetId = this.buildNoteTargetId(nodeId);
    if (!targetId) {
      return null;
    }

    const pendingDraft = this.pendingNoteDrafts.get(nodeId);
    if (typeof pendingDraft === 'string' && pendingDraft.trim()) {
      return pendingDraft;
    }

    const cached = this.annotationLinkCache.get(targetId);
    if (cached?.annotationId) {
      try {
        const annotation = await firstValueFrom(
          this.annotationService.get(cached.annotationId),
        );
        return annotation.text?.trim() ?? null;
      } catch {
        return null;
      }
    }

    const mindMapId = this.currentMindMap?.id ?? null;
    if (!mindMapId) {
      return null;
    }

    try {
      const existingLinks = await firstValueFrom(
        this.annotationLinkService.listByTarget({ mindMapId, nodeKey: nodeId }),
      );
      const link = this.pickValidLink(existingLinks);
      if (!link?.annotationId) {
        return null;
      }

      this.annotationLinkCache.set(targetId, link);
      const annotation = await firstValueFrom(
        this.annotationService.get(link.annotationId),
      );
      return annotation.text?.trim() ?? null;
    } catch {
      return null;
    }
  }

  private applyRefineNote(nodeId: string, noteText: string): void {
    const service = this.getFlashMapService();
    const node = service?.getNode(nodeId);
    if (node) {
      const nodeData = (node.data ?? {}) as Record<string, unknown>;
      const currentNote =
        (nodeData['note'] as Record<string, unknown> | undefined) ?? {};
      nodeData['note'] = {
        ...currentNote,
        text: noteText,
      };
      node.data = nodeData;
      service?.refreshNode(nodeId);
    }

    this.pendingNoteDrafts.set(nodeId, noteText);
    this.noteSyncQueue.next(nodeId);
    this.toastr.success('Note refined and saved.');
  }

  private formatRefinedNoteForEditor(value: string): string {
    if (!value?.trim()) {
      return '';
    }

    const normalized = value.replace(/\r\n?/g, '\n');
    if (this.looksLikeHtml(normalized)) {
      return normalized;
    }

    const paragraphBreakToken = '[[[PARABREAK6F1E0A]]]';
    const inlineBreakToken = '[[[LINEBREAK6F1E0A]]]';
    const withBreakTokens = normalized
      .replace(/\n{2,}/g, paragraphBreakToken)
      .replace(/\n/g, inlineBreakToken);
    const parsed = MarkdownParser.parse(withBreakTokens);
    const paragraphs = parsed
      .split(paragraphBreakToken)
      .map((paragraph) => paragraph.trim())
      .filter((paragraph) => paragraph.length > 0)
      .map(
        (paragraph) =>
          `<p>${paragraph.replaceAll(inlineBreakToken, '<br>')}</p>`,
      );

    return paragraphs.join('');
  }

  private looksLikeHtml(value: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(value);
  }

  private buildDeckCreatePayload(
    workspaceId: number,
    mindMapId: number,
    nodeKey: string,
  ): DeckCreatePayload {
    const service = this.getFlashMapService();
    const selectedNode = service?.getNode(nodeKey);
    const nodeTopic = selectedNode?.topic?.trim() ?? '';
    const title = nodeTopic || this.i18n.tr('deckFallbackTitle', { nodeKey });

    return {
      workspaceId,
      mindMapId,
      nodeKey,
      title,
      description: '',
      flashcardCount: 0,
    };
  }

  private openTrainingModeModal(
    deck: DeckModel,
    dueCount: number | null,
  ): void {
    const totalCards = toNonNegativeMetric(deck.flashcardCount);
    const dueDisabled = totalCards <= 0 || (dueCount != null && dueCount <= 0);
    const dueOption: TrainingModeModalOption = {
      id: 'due',
      title: this.deckI18n.tr('train.modeDue'),
      description: this.deckI18n.tr('train.modeDueDescription'),
      count: dueCount ?? totalCards,
      disabled: dueDisabled,
      disabledReason: dueDisabled
        ? this.deckI18n.tr('train.modeDueDisabledNoCards')
        : undefined,
      icon: 'target',
    };

    const allOption: TrainingModeModalOption = {
      id: 'all',
      title: this.deckI18n.tr('train.modeAll'),
      description: this.deckI18n.tr('train.modeAllDescription'),
      count: totalCards,
      disabled: totalCards <= 0,
      disabledReason:
        totalCards <= 0
          ? this.deckI18n.tr('train.modeAllDisabledNoCards')
          : undefined,
      icon: 'collection',
    };
    const createFlashcardsOption: TrainingModeModalOption = {
      id: 'create-flashcards',
      title: this.deckI18n.tr('train.modeCreateFlashcards'),
      description: this.deckI18n.tr('train.modeCreateFlashcardsDescription'),
      icon: 'plus-circle',
    };

    const modalRef = this.modalService.open(TrainingModeModal, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'training-mode-window',
    });
    const modalInstance = modalRef.componentInstance as TrainingModeModal;
    modalInstance.configure({
      title: this.deckI18n.tr('train.modeModalTitle'),
      subtitle: this.deckI18n.tr('train.modeModalDescription'),
      cancelLabel: this.deckI18n.tr('common.cancel'),
      options:
        totalCards <= 0
          ? [dueOption, allOption, createFlashcardsOption]
          : [dueOption, allOption],
    });

    modalRef.closed.pipe(take(1)).subscribe((mode) => {
      this.handleTrainingModeSelection(deck.id, mode);
    });
  }

  private handleTrainingModeSelection(
    deckId: number,
    mode: TrainingModeModalResult | null | undefined,
  ): void {
    if (mode === 'create-flashcards') {
      this.navigateToCreateFlashcards(deckId);
      return;
    }

    this.navigateToTrainingMode(deckId, mode);
  }

  private navigateToTrainingMode(
    deckId: number,
    mode: TrainingModeModalResult | null | undefined,
  ): void {
    if (mode !== 'due' && mode !== 'all') {
      return;
    }

    const mindMapId = this.currentMindMap?.id ?? null;
    this.persistMindMapThen(() => {
      this.router.navigate(['/deck', deckId, 'train'], {
        queryParams: {
          trainMode: mode,
          from: 'mindmap-edit',
          ...(mindMapId ? { mindMapId } : {}),
        },
      });
    });
  }

  private navigateToCreateFlashcards(deckId: number): void {
    const mindMapId = this.currentMindMap?.id ?? null;
    this.persistMindMapThen(() => {
      this.router.navigate(['/deck-edit', deckId], {
        queryParams: {
          openNewFlashcard: 1,
          from: 'mindmap-edit',
          ...(mindMapId ? { mindMapId } : {}),
        },
      });
    });
  }

  private restoreMindMapVersion(version: MindMapVersionModel): void {
    if (this.restoringVersionId !== null) {
      return;
    }

    this.ensureMindMapCreationAllowed()
      .pipe(
        switchMap((allowed) => {
          if (!allowed) {
            return EMPTY;
          }

          this.restoringVersionId = version.id;
          const modalRef = this.modalService.open(RestoreVersionConfirmModal, {
            centered: true,
            backdrop: 'static',
            windowClass: 'modal-alert modal-restore-alert',
          });

          return from(modalRef.result as Promise<boolean>).pipe(
            catchError(() => of(false)),
            switchMap((confirmed) => {
              if (!confirmed) {
                return EMPTY;
              }

              this.isRestoringVersion = true;
              return this.mindMapVersionService.restore(version.id).pipe(
                switchMap((mindmap) => {
                  if (!mindmap.data) {
                    this.toastr.error(
                      this.i18n.tr('errorRestoringMindMapVersion'),
                    );
                    return EMPTY;
                  }

                  return from(this.applyMindMapData(mindmap.data)).pipe(
                    map((applied) => ({ mindmap, applied })),
                  );
                }),
              );
            }),
          );
        }),
        finalize(() => {
          this.restoringVersionId = null;
          this.isRestoringVersion = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ mindmap, applied }) => {
          if (!applied) {
            return;
          }

          this.currentMindMap = mindmap;
          this.resetMindMapSaveState();
          this.resetNoteSyncState();
          this.loadNodeMetrics(mindmap.id);
          this.toastr.success(this.i18n.tr('mindMapVersionRestored'));
          this.refreshAccountPlanLimits();
        },
        error: (error: unknown) => {
          if (isMaxMindMapsLimitError(error)) {
            this.showMindMapLimitReached();
            this.refreshAccountPlanLimits();
            return;
          }

          this.toastr.error(this.i18n.tr('errorRestoringMindMapVersion'));
        },
      });
  }

  private copyMindMapVersion(version: MindMapVersionModel): void {
    const currentMindMap = this.currentMindMap;
    const workspaceId =
      currentMindMap?.workspaceId ?? this.authService.GetWorkspace();

    if (!currentMindMap || !workspaceId) {
      this.toastr.error(this.i18n.tr('mapContextUnavailable'));
      return;
    }

    this.ensureMindMapCreationAllowed()
      .pipe(
        switchMap((allowed) => {
          if (!allowed) {
            return EMPTY;
          }

          return this.mindMapVersionService.get(version.id).pipe(
            switchMap((versionDetail) => {
              if (!versionDetail.data) {
                this.toastr.error(this.i18n.tr('errorLoadingMindMapVersion'));
                return of(null);
              }

              const payload: MindMapCreatePayload = {
                workspaceId,
                title: `${currentMindMap.title.trim()} copy`,
                data: versionDetail.data,
                visibility: currentMindMap.visibility,
              };

              return this.mindMapService.create(payload);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (copiedMindMap) => {
          if (!copiedMindMap) {
            return;
          }

          this.refreshAccountPlanLimits();
          this.router.navigate(['/home']);
        },
        error: (error: unknown) => {
          if (isMaxMindMapsLimitError(error)) {
            this.showMindMapLimitReached();
            this.refreshAccountPlanLimits();
            return;
          }

          this.toastr.error(
            this.i18n.tr('errorDuplicateItem', {
              itemType: this.i18n.tr('itemTypeMindmap'),
            }),
          );
        },
      });
  }

  private openShareModal(): void {
    const mindMapId = this.currentMindMap?.id ?? null;
    const workspaceId =
      this.currentMindMap?.workspaceId ??
      this.authService.GetWorkspace() ??
      null;

    if (!mindMapId || !workspaceId) {
      return;
    }

    const modalRef = this.modalService.open(ShareSettingModal, {
      size: 'lg',
      backdrop: 'static',
    });
    modalRef.componentInstance.mindMapId = mindMapId;
    modalRef.componentInstance.workspaceId = workspaceId;
    modalRef.componentInstance.mindMapTitle =
      this.currentMindMap?.title ?? this.i18n.tr('fallbackMapTitle');
    modalRef.componentInstance.mindMapPublicId =
      this.currentMindMap?.publicId ?? null;
  }

  private openInviteModal(): void {
    const mindMapId = this.currentMindMap?.id ?? null;
    const workspaceId =
      this.currentMindMap?.workspaceId ??
      this.authService.GetWorkspace() ??
      null;

    if (!mindMapId || !workspaceId) {
      return;
    }

    const modalRef = this.modalService.open(InviteSettingModal, {
      size: 'lg',
      backdrop: 'static',
    });
    modalRef.componentInstance.mindMapId = mindMapId;
    modalRef.componentInstance.workspaceId = workspaceId;
    modalRef.componentInstance.accountId = this.authService.GetAccount();
  }

  handleMapShow(): void {
    this.debugMindMapRenderState('flash-map emitted onShow');
    const hasMindMapId = !!this.route.snapshot.queryParamMap.get('id');
    if (this.shouldFocusRoot && hasMindMapId) {
      this.focusRootForEdit();
    }
  }

  retryMindMapLoad(): void {
    const mindMapId = this.requestedMindMapId;
    if (!mindMapId) {
      return;
    }

    this.loadMindMap(mindMapId);
  }

  handleMapEdit(event?: FlashMapEditEvent): void {
    if (this.isRestoringVersion || !this.canUpdateMindMap) {
      return;
    }

    if (this.isEmptyNewNodeEvent(event)) {
      return;
    }

    this.requestMindMapAutosave();
  }

  openNodeImagePicker(event: { nodeId: string }): void {
    if (!this.canUpdateMindMap) {
      return;
    }
    const nodeId = event.nodeId?.trim();
    if (!nodeId || !this.getFlashMapService()?.getNode(nodeId)) return;
    const requestedCount =
      this.countMindMapImageAttachments() +
      (this.nodeHasImageAttachment(nodeId) ? 0 : 1);

    this.accountPlanLimitService
      .canUseMindMapAttachmentsFresh(requestedCount)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allowed) => {
        if (!allowed) {
          this.showMindMapAttachmentsLimitReached();
          return;
        }

        if (!this.getFlashMapService()?.getNode(nodeId)) return;

        const modalRef = this.modalService.open(MediaPickerModalComponent, {
          size: 'lg',
          centered: true,
          backdrop: 'static',
        });
        modalRef.componentInstance.initialTab = 'library';
        modalRef.componentInstance.imageOnly = true;
        modalRef.result.then(
          (media: EditorMedia) => {
            const assetId = Number(media.workspaceAssetId ?? media.id);
            if (!Number.isSafeInteger(assetId) || assetId <= 0 || !media.url)
              return;
            this.getFlashMapService()?.setNodeImage(nodeId, {
              src: media.url,
              workspaceAssetId: assetId,
              position: media.source === 'ai' ? 'top' : 'left',
              ...(media.source === 'ai' ? { width: 144, height: 96 } : {}),
            });
            this.requestMindMapAutosave();
          },
          () => undefined,
        );
      });
  }

  handleDrawingChange(document: FlashMapDrawingDocument): void {
    if (this.isRestoringVersion || !this.canUpdateMindMap) {
      return;
    }
    this.drawingDocument.set(document);
    this.requestMindMapAutosave();
  }

  private isEmptyNewNodeEvent(event?: FlashMapEditEvent): boolean {
    if (event?.evt !== 'add_node') {
      return false;
    }

    const nodeId = event.node?.trim() ?? '';
    const nodeTopic = nodeId
      ? this.getFlashMapService()?.getNode?.(nodeId)?.topic
      : undefined;
    if (typeof nodeTopic === 'string') {
      return nodeTopic.trim().length === 0;
    }

    const topic = event.data?.[2];
    return typeof topic === 'string' && topic.trim().length === 0;
  }

  handleNoteDraftChanged(event: NoteDraftChangedEvent): void {
    if (this.isRestoringVersion || !this.canUpdateMindMap) {
      return;
    }

    const nodeId = event.nodeId?.trim() ?? '';
    if (!nodeId) {
      return;
    }

    this.applyNoteDraftToNode(nodeId, event.text ?? '');
    this.requestMindMapAutosave();

    this.pendingNoteDrafts.set(nodeId, event.text ?? '');
    this.noteSyncDebounceRequests.next();
  }

  handleNoteFlushRequested(event: NoteFlushRequestedEvent): void {
    const nodeId = event.nodeId?.trim() || null;
    this.noteSyncQueue.next(nodeId);
    if (this.hasPendingMindMapChanges()) {
      this.saveRequests.next({ flush: true });
    }
  }

  private applyNoteDraftToNode(nodeId: string, text: string): void {
    const service = this.getFlashMapService();
    const node = service?.getNode(nodeId);
    if (!node) {
      return;
    }

    const nodeData = (node.data ?? {}) as Record<string, unknown>;
    if (this.hasMeaningfulNoteText(text)) {
      nodeData['note'] = { text };
    } else {
      delete nodeData['note'];
      const nestedData = nodeData['data'];
      if (nestedData && typeof nestedData === 'object') {
        delete (nestedData as Record<string, unknown>)['note'];
      }
    }

    node.data = nodeData;
    service?.refreshNode(nodeId);
  }

  private loadMindMap(id: number): void {
    const requestId = ++this.mindMapLoadRequestId;
    this.mindMapDataApplyRequestId += 1;
    const startedAt = performance.now();
    this.requestedMindMapId = id;
    this.mindMapLoadState.set('loading');
    this.mindMapLoadError.set(null);

    this.debugMindMapRender('mind map request started', { id, requestId });

    if (this.currentMindMap?.id !== id) {
      this.revokeMindMapImageObjectUrls();
      this.currentMindMap = null;
      this.mindData.set(null);
      this.masterySummary.set(null);
      this.resetNoteSyncState();
    }

    this.accountPlanLimitService.loadIfMissing().pipe(take(1)).subscribe();
    defer(() => this.mindMapService.get(id))
      .pipe(
        retry({
          count: 1,
          delay: (error: unknown) =>
            this.isTransientMindMapLoadError(error)
              ? of(void 0)
              : throwError(() => error),
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (mindmap) => {
          if (!this.isCurrentMindMapLoadRequest(id, requestId)) {
            this.debugMindMapRender('ignoring stale mind map response', {
              id,
              requestId,
              activeRequestId: this.mindMapLoadRequestId,
              durationMs: Math.round(performance.now() - startedAt),
            });
            return;
          }

          this.debugMindMapRender('mind map response received', {
            id,
            requestId,
            responseId: mindmap.id,
            durationMs: Math.round(performance.now() - startedAt),
            serializedDataLength: mindmap.data?.length ?? 0,
          });

          if (this.currentMindMap?.id !== mindmap.id) {
            this.resetNoteSyncState();
          }
          this.currentMindMap = mindmap;
          this.resetMindMapSaveState();
          void this.applyMindMapData(mindmap.data).then((applied) => {
            if (!applied || !this.isCurrentMindMapLoadRequest(id, requestId)) {
              return;
            }

            const data = this.mindData();
            const tree = data?.data as MindMapTreeNode | null;
            this.debugMindMapRender(
              'mind map data assigned to flash-map input',
              {
                id,
                requestId,
                format: data?.format ?? null,
                rootId: tree?.id ?? null,
                rootChildren: Array.isArray(tree?.children)
                  ? tree.children.length
                  : 0,
              },
            );
            this.logMindMapRenderAfterNextFrame(id, requestId);
          });
          this.loadNodeMetrics(mindmap.id);
          this.mindMapLoadState.set('ready');
        },
        error: (error: unknown) => {
          if (!this.isCurrentMindMapLoadRequest(id, requestId)) {
            return;
          }

          this.debugMindMapRender('mind map request failed', {
            id,
            requestId,
            durationMs: Math.round(performance.now() - startedAt),
            status: error instanceof ApiError ? error.status : null,
            errorType: error instanceof Error ? error.name : typeof error,
          });
          this.mindMapLoadState.set('error');
          this.mindMapLoadError.set(this.i18n.tr('errorLoadingMindMap'));
        },
      });
  }

  private logMindMapRenderAfterNextFrame(id: number, requestId: number): void {
    requestAnimationFrame(() => {
      if (
        this.destroyRef.destroyed ||
        !this.isCurrentMindMapLoadRequest(id, requestId)
      ) {
        return;
      }

      this.debugMindMapRenderState(
        'flash-map render check after data assignment',
        {
          id,
          requestId,
        },
      );
    });
  }

  private isMindMapRootOutsideViewport(): boolean {
    const container = this.getFlashMapService()?.getContainer() ?? null;
    const root = container?.querySelector<HTMLElement>('jmnode[nodeid="root"]');
    if (!container || !root) {
      return false;
    }

    const containerRect = container.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    if (containerRect.width <= 0 || containerRect.height <= 0) {
      return false;
    }

    return (
      rootRect.right < containerRect.left ||
      rootRect.left > containerRect.right ||
      rootRect.bottom < containerRect.top ||
      rootRect.top > containerRect.bottom
    );
  }

  private debugMindMapRenderState(
    step: string,
    payload: Record<string, unknown> = {},
  ): void {
    const flashMapService = this.getFlashMapService();
    const container = flashMapService?.getContainer() ?? null;
    const editor =
      container?.querySelector<HTMLElement>('.jsmind-editor') ?? null;
    const root = container?.querySelector<HTMLElement>('jmnode[nodeid="root"]');
    const containerRect = container?.getBoundingClientRect();
    const rootRect = root?.getBoundingClientRect();
    const editorRect = editor?.getBoundingClientRect();

    this.debugMindMapRender(step, {
      ...payload,
      loadState: this.mindMapLoadState(),
      hasFlashMapComponent: Boolean(this.flashMap),
      flashMapReady: flashMapService?.isReady() ?? false,
      renderedNodeCount: container?.querySelectorAll('jmnode').length ?? 0,
      containerSize: containerRect
        ? `${Math.round(containerRect.width)}x${Math.round(containerRect.height)}`
        : null,
      rootPosition: rootRect
        ? `${Math.round(rootRect.left)},${Math.round(rootRect.top)}`
        : null,
      rootSize: rootRect
        ? `${Math.round(rootRect.width)}x${Math.round(rootRect.height)}`
        : null,
      rootOutsideViewport: this.isMindMapRootOutsideViewport(),
      editorSize: editorRect
        ? `${Math.round(editorRect.width)}x${Math.round(editorRect.height)}`
        : null,
    });
  }

  private debugMindMapRender(
    step: string,
    payload: Record<string, unknown> = {},
  ): void {
    console.log(`[MindmapEdit] ${step}`, payload);
  }

  private parseMindMapId(value: string): number | null {
    if (!/^[1-9]\d*$/.test(value)) {
      return null;
    }

    const id = Number(value);
    return Number.isSafeInteger(id) ? id : null;
  }

  private isTransientMindMapLoadError(error: unknown): boolean {
    if (!(error instanceof ApiError)) {
      return true;
    }

    return error.status === 0 || error.status >= 500;
  }

  private isCurrentMindMapLoadRequest(id: number, requestId: number): boolean {
    return (
      this.requestedMindMapId === id && this.mindMapLoadRequestId === requestId
    );
  }

  private resetMindMapLoadState(): void {
    this.requestedMindMapId = null;
    this.mindMapLoadRequestId += 1;
    this.mindMapLoadState.set('idle');
    this.mindMapLoadError.set(null);
  }

  private showMindMapLoadError(message: string): void {
    this.requestedMindMapId = null;
    this.mindMapLoadRequestId += 1;
    this.mindMapDataApplyRequestId += 1;
    this.mindMapLoadState.set('error');
    this.mindMapLoadError.set(message);
  }

  private createMindMapFromContext(): void {
    if (this.isCreating || !this.canCreateMindMap) {
      return;
    }

    const workspaceId = this.authService.GetWorkspace();
    if (!workspaceId) {
      return;
    }

    const initialData = this.buildNewMindMapData();
    const payload: MindMapCreatePayload = {
      workspaceId,
      title: this.i18n.tr('fallbackMapTitle'),
      data: JSON.stringify(initialData),
      visibility: 0,
    };

    this.isCreating = true;
    this.ensureMindMapCreationAllowed()
      .pipe(
        switchMap((allowed) =>
          allowed ? this.mindMapService.create(payload) : EMPTY,
        ),
        finalize(() => {
          this.isCreating = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (mindmap) => {
          this.resetNoteSyncState();
          this.masterySummary.set(null);
          this.currentMindMap = mindmap;
          this.resetMindMapSaveState();
          const data = this.parseMindMapData(mindmap.data);
          const tree = data.data as MindMapTreeNode | null;
          if (tree && typeof tree === 'object') {
            applyDepthCollapse(tree, MAX_PATH_NODES);
          }
          this.mindData.set(data);
          const fromRoute = this.route.snapshot.queryParamMap.get('fromRoute');
          const queryParams: Record<string, string | number> = {
            id: mindmap.id,
            new: 1,
          };
          if (fromRoute) {
            queryParams['fromRoute'] = fromRoute;
          }

          this.refreshAccountPlanLimits();
          this.router.navigate(['/mindmap-edit'], {
            queryParams,
            replaceUrl: true,
          });
        },
        error: (error: unknown) => {
          if (isMaxMindMapsLimitError(error)) {
            this.showMindMapLimitReached();
            this.refreshAccountPlanLimits();
          }
        },
      });
  }

  private async applyMindMapData(
    rawData: string | null | undefined,
  ): Promise<boolean> {
    const requestId = ++this.mindMapDataApplyRequestId;
    const data = this.parseMindMapData(rawData);
    this.applyMindMapAppearance(data);
    const tree = data.data as MindMapTreeNode | null;
    if (tree && typeof tree === 'object') {
      applyDepthCollapse(tree, MAX_PATH_NODES);
    }

    const imageObjectUrls = new Set<string>();
    if (this.hasMindMapImagesToHydrate(data.data as MindMapTreeNode | null)) {
      await this.hydrateMindMapImageUrls(data, imageObjectUrls);
    }

    if (requestId !== this.mindMapDataApplyRequestId) {
      this.revokeObjectUrls(imageObjectUrls);
      return false;
    }

    this.revokeMindMapImageObjectUrls();
    for (const url of imageObjectUrls) {
      this.mindMapImageObjectUrls.add(url);
    }
    this.mindData.set(data);
    this.drawingDocument.set(data.drawings ?? { version: 1, strokes: [] });
    return true;
  }

  private loadNodeMetrics(mindMapId: number): void {
    if (!Number.isFinite(mindMapId) || mindMapId <= 0) {
      this.masterySummary.set(null);
      return;
    }

    this.mindMapService
      .getMastery(mindMapId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summary) => this.masterySummary.set(summary),
        error: () => {
          this.masterySummary.set(null);
        },
      });
  }

  private buildNodeMetric(metrics: {
    flashcards: number;
    dueCards: number;
    studiedCards: number;
    matureCards: number;
  }): { text: string; dueText?: string } | null {
    const dueCards = toNonNegativeMetric(metrics.dueCards);
    const flashcards = toNonNegativeMetric(metrics.flashcards);

    if (dueCards > 0 && flashcards > 0) {
      return {
        dueText: this.i18n.tr('nodeMetricsDue', { due: dueCards }),
        text: this.i18n.tr('nodeMetricsCards', { cards: flashcards }),
      };
    }

    if (dueCards > 0) {
      return {
        text: '',
        dueText: this.i18n.tr('nodeMetricsDue', { due: dueCards }),
      };
    }

    if (flashcards > 0) {
      return { text: this.i18n.tr('nodeMetricsCards', { cards: flashcards }) };
    }

    return null;
  }

  private buildNodeMetricTooltipText(metrics: {
    flashcards: number;
    studiedCards: number;
    matureCards: number;
  }): {
    masteryText: string;
    studiedText: string;
    ariaLabel: string;
  } {
    const total = toNonNegativeMetric(metrics.flashcards);
    const studied = Math.min(total, toNonNegativeMetric(metrics.studiedCards));
    const mature = Math.min(total, toNonNegativeMetric(metrics.matureCards));
    const percentage =
      total > 0 ? Math.min(100, Math.round((mature / total) * 100)) : 0;
    const parameters = { percentage, studied, total };

    return {
      masteryText: this.i18n.tr('nodeMetricsTooltipMastery', parameters),
      studiedText: this.i18n.tr('nodeMetricsTooltipStudied', parameters),
      ariaLabel: this.i18n.tr('nodeMetricsTooltipAriaLabel', parameters),
    };
  }

  private buildRootMetric(
    summary: MindMapMasteryModel,
  ): FlashMapNodeMetricsMap[string] {
    if (
      !summary.root ||
      summary.root.flashcards <= 0 ||
      summary.root.masteryPercentage <= 0 ||
      !summary.branches?.length
    ) {
      return null;
    }
    const chartItems = summary.branches
      .filter((branch) => branch.nodeKey && branch.title)
      .map((branch) => ({
        label: branch.title,
        percentage: Math.min(
          100,
          Math.max(0, Math.round(branch.masteryPercentage)),
        ),
      }));
    if (chartItems.length === 0) {
      return null;
    }

    const itemsLabel = chartItems
      .map((item) => `${item.label}: ${item.percentage}%`)
      .join('; ');
    return {
      text: this.i18n.tr('nodeMetricsTooltipMastery', {
        percentage: summary.root.masteryPercentage,
      }),
      rootChart: {
        items: chartItems,
        ariaLabel: this.i18n.tr('rootMetricsChartAriaLabel', {
          items: itemsLabel,
        }),
      },
    };
  }

  private toPersistedMindMapData(data: StoredMindMapData): StoredMindMapData {
    const persisted = structuredClone(data);
    const clearRuntimeUrls = (node: MindMapTreeNode | null): void => {
      if (!node) return;
      const image = node.image;
      if (image?.workspaceAssetId) delete image.src;
      for (const child of node.children ?? []) clearRuntimeUrls(child);
    };
    clearRuntimeUrls(persisted.data as MindMapTreeNode | null);
    return persisted;
  }

  private async hydrateMindMapImageUrls(
    data: StoredMindMapData,
    imageObjectUrls: Set<string>,
  ): Promise<void> {
    const urlsByAssetId = new Map<number, Promise<string | null>>();
    const resolveAssetUrl = (assetId: number): Promise<string | null> => {
      const existing = urlsByAssetId.get(assetId);
      if (existing) return existing;
      const request = firstValueFrom(
        this.workspaceAssetService.getContentBlob(assetId),
      )
        .then((blob) => {
          const url = this.workspaceAssetService.createObjectUrl(blob);
          imageObjectUrls.add(url);
          return url;
        })
        .catch(() => null);
      urlsByAssetId.set(assetId, request);
      return request;
    };
    const hydrate = async (node: MindMapTreeNode | null): Promise<void> => {
      if (!node) return;
      const image = node.image;
      const assetId = Number(image?.workspaceAssetId);
      if (image && Number.isSafeInteger(assetId) && assetId > 0) {
        const url = await resolveAssetUrl(assetId);
        if (url) image.src = url;
        else delete node.image;
      }
      await Promise.all((node.children ?? []).map((child) => hydrate(child)));
    };
    await hydrate(data.data as MindMapTreeNode | null);
  }

  private hasMindMapImagesToHydrate(node: MindMapTreeNode | null): boolean {
    if (!node) {
      return false;
    }

    const assetId = Number(node.image?.workspaceAssetId);
    if (Number.isSafeInteger(assetId) && assetId > 0) {
      return true;
    }

    return (node.children ?? []).some((child) =>
      this.hasMindMapImagesToHydrate(child),
    );
  }

  private revokeMindMapImageObjectUrls(): void {
    this.revokeObjectUrls(this.mindMapImageObjectUrls);
    this.mindMapImageObjectUrls.clear();
  }

  private revokeObjectUrls(urls: Iterable<string>): void {
    for (const url of urls) {
      globalThis.URL?.revokeObjectURL(url);
    }
  }

  private parseMindMapData(
    rawData: string | null | undefined,
  ): StoredMindMapData {
    if (!rawData) {
      return this.buildFallbackMindMap();
    }

    try {
      const parsed = JSON.parse(rawData) as StoredMindMapData;
      if (parsed?.format && parsed?.data) {
        return parsed;
      }
    } catch {
      return this.buildFallbackMindMap();
    }

    return this.buildFallbackMindMap();
  }

  private applyMindMapAppearance(data: StoredMindMapData): void {
    const appearance = data.appearance;
    const theme = appearance?.theme;
    const layout = appearance?.layout;

    this.flashMapVisualTheme.set(
      MINDMAP_VISUAL_THEMES.includes(theme as MindmapVisualTheme)
        ? (theme as MindmapVisualTheme)
        : DEFAULT_MINDMAP_APPEARANCE.theme,
    );
    this.setFlashMapLayout(
      MINDMAP_LAYOUTS.includes(layout as MindmapLayout)
        ? (layout as MindmapLayout)
        : DEFAULT_MINDMAP_APPEARANCE.layout,
    );
  }

  private buildFallbackMindMap(): StoredMindMapData {
    return {
      meta: {
        name: 'mindmap',
        author:
          this.authService.getUserName() ?? this.i18n.tr('fallbackMetaAuthor'),
        version: '1.0',
      },
      format: 'node_tree',
      data: {
        id: 'root',
        topic: this.i18n.tr('fallbackMapTitle'),
      },
    };
  }

  private buildNewMindMapData(): StoredMindMapData {
    return {
      meta: {
        name: 'mindmap',
        author:
          this.authService.getUserName() ?? this.i18n.tr('fallbackMetaAuthor'),
        version: '1.0',
      },
      format: 'node_tree',
      data: {
        id: 'root',
        topic: this.i18n.tr('fallbackMapTitle'),
      },
    };
  }

  private navigateBackToOrigin(): void {
    const params = this.route.snapshot.queryParamMap;
    const fromRoute = params.get('fromRoute');

    if (fromRoute === 'home') {
      this.router.navigate(['/home']);
      return;
    }

    if (fromRoute === 'mindmap') {
      this.router.navigate(['/mindmap']);
      return;
    }

    if (fromRoute === 'deck' && this.currentMindMap?.id) {
      this.router.navigate(['/deck'], {
        queryParams: {
          mindMapId: this.currentMindMap.id,
          from: 'mindmap-edit',
        },
      });
      return;
    }

    this.router.navigate(['/mindmap']);
  }

  private duplicateMindMap(): void {
    if (this.isDuplicating) {
      return;
    }

    const sourceMindMapId = this.currentMindMap?.id;
    if (!sourceMindMapId) {
      return;
    }

    this.isDuplicating = true;
    this.ensureMindMapCreationAllowed()
      .pipe(
        switchMap((allowed) => {
          if (!allowed) {
            return EMPTY;
          }

          return this.persistLatestPendingMindMapUpdate().pipe(
            switchMap(() => this.mindMapService.copy(sourceMindMapId)),
          );
        }),
        finalize(() => {
          this.isDuplicating = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (copiedMindMap) => {
          this.refreshAccountPlanLimits();
          this.router.navigate(['/home']);
        },
        error: (error: unknown) => {
          if (isMaxMindMapsLimitError(error)) {
            this.showMindMapLimitReached();
            this.refreshAccountPlanLimits();
          }
        },
      });
  }

  private ensureMindMapCreationAllowed(): Observable<boolean> {
    return this.accountPlanLimitService.canCreateMindMapFresh().pipe(
      map((canCreate) => {
        if (!canCreate) {
          this.showMindMapLimitReached();
        }

        return canCreate;
      }),
      catchError(() => of(true)),
    );
  }

  private showMindMapLimitReached(): void {
    this.planLimitNotificationService.showPlanLimit('MaxMindMaps');
  }

  private showMindMapAttachmentsLimitReached(): void {
    this.planLimitNotificationService.showPlanLimit('MaxMindMapsAttachments');
  }

  showMindMapNotesLimitReached(): void {
    this.planLimitNotificationService.showPlanLimit('EnableMindmapNotes');
  }

  showMindMapExportLimitReached(): void {
    this.planLimitNotificationService.showPlanLimit('EnableMindmapExport');
  }

  showMindMapShareLimitReached(): void {
    this.planLimitNotificationService.showPlanLimit('EnableShareMindmaps');
  }

  showMindMapInviteLimitReached(): void {
    this.planLimitNotificationService.showPlanLimit('EnableInviteMindmaps');
  }

  private showDeckLimitReached(): void {
    this.planLimitNotificationService.showPlanLimit('MaxDecks');
  }

  private refreshAccountPlanLimits(): void {
    this.accountPlanLimitService
      .refresh()
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  private countMindMapImageAttachments(): number {
    const data = this.getFlashMapService()?.getData('node_tree') as
      | MindMapData
      | undefined;
    return this.countNodeImageAttachments(data?.data as MindMapTreeNode | null);
  }

  private countNodeImageAttachments(node: MindMapTreeNode | null): number {
    if (!node) {
      return 0;
    }

    const imageAssetId = Number(node.image?.workspaceAssetId);
    const currentNodeCount =
      Number.isSafeInteger(imageAssetId) && imageAssetId > 0 ? 1 : 0;
    return (
      currentNodeCount +
      (node.children?.reduce(
        (count, child) => count + this.countNodeImageAttachments(child),
        0,
      ) ?? 0)
    );
  }

  private nodeHasImageAttachment(nodeId: string): boolean {
    const data = this.getFlashMapService()?.getData('node_tree') as
      | MindMapData
      | undefined;
    return (
      this.findMindMapNode(data?.data as MindMapTreeNode | null, nodeId)?.image
        ?.workspaceAssetId != null
    );
  }

  private findMindMapNode(
    node: MindMapTreeNode | null,
    nodeId: string,
  ): MindMapTreeNode | null {
    if (!node) {
      return null;
    }

    if (node.id === nodeId) {
      return node;
    }

    for (const child of node.children ?? []) {
      const found = this.findMindMapNode(child, nodeId);
      if (found) {
        return found;
      }
    }

    return null;
  }

  private requestMindMapAutosave(): void {
    if (!this.canUpdateMindMap) {
      return;
    }
    this.mindMapChangeRevision += 1;
    this.updateRequests.next();
  }

  private hasPendingMindMapChanges(): boolean {
    return this.mindMapChangeRevision > this.persistedMindMapChangeRevision;
  }

  private resetMindMapSaveState(): void {
    this.mindMapChangeRevision = 0;
    this.persistedMindMapChangeRevision = 0;
  }

  private persistMindMapUpdate(): Observable<boolean> {
    if (this.isRestoringVersion) {
      return of(false);
    }

    if (!this.currentMindMap) {
      return of(false);
    }

    const service = this.getFlashMapService();
    if (!service) {
      return of(false);
    }

    const nodeTreeData = service.getData('node_tree') as MindMapData;
    if (this.hasEmptyNonRootMindMapNode(nodeTreeData)) {
      return of(false);
    }

    const revision = this.mindMapChangeRevision;

    const data: StoredMindMapData = {
      ...nodeTreeData,
      drawings: this.flashMap?.getDrawingDocument() ?? this.drawingDocument(),
      appearance: {
        layout: this.flashMapLayout(),
        theme: this.flashMapVisualTheme(),
      },
    };
    const persistedData = this.toPersistedMindMapData(data);
    const rootTopic = service.getRoot()?.topic ?? this.currentMindMap.title;

    const payload: MindMapUpdatePayload = {
      id: this.currentMindMap.id,
      publicId: this.currentMindMap.publicId,
      workspaceId: this.currentMindMap.workspaceId,
      title: rootTopic,
      data: JSON.stringify(persistedData),
      visibility: this.currentMindMap.visibility,
      isActive: this.currentMindMap.isActive ?? true,
    };

    return this.mindMapService.update(payload).pipe(
      tap((updated) => {
        if (updated) {
          this.currentMindMap = updated;
          this.persistedMindMapChangeRevision = Math.max(
            this.persistedMindMapChangeRevision,
            revision,
          );
        }
      }),
      map(() => true),
      catchError((error: unknown) => {
        if (isMaxMindMapsAttachmentsLimitError(error)) {
          this.accountPlanLimitService.refresh().subscribe();
          this.showMindMapAttachmentsLimitReached();
          return of(false);
        }

        this.toastr.error(this.i18n.tr('errorSavingMindMap'));
        return of(false);
      }),
    );
  }

  private persistPendingMindMapUpdate(): Observable<boolean> {
    return defer(() => {
      if (!this.hasPendingMindMapChanges()) {
        return of(true);
      }

      return this.persistMindMapUpdate();
    });
  }

  private persistLatestPendingMindMapUpdate(): Observable<boolean> {
    return this.persistPendingMindMapUpdate().pipe(
      switchMap((saved) =>
        saved && this.hasPendingMindMapChanges()
          ? this.persistLatestPendingMindMapUpdate()
          : of(saved),
      ),
    );
  }

  private persistMindMapThen(onDone: () => void, onFailed?: () => void): void {
    if (!this.hasPendingMindMapChanges()) {
      onDone();
      return;
    }

    this.saveRequests.next({ flush: true, onDone, onFailed });
  }

  private hasEmptyNonRootMindMapNode(data: MindMapData): boolean {
    const hasEmptyChild = (node: MindMapTreeNode | null): boolean => {
      if (!node) {
        return false;
      }

      return (node.children ?? []).some(
        (child) =>
          typeof child.topic !== 'string' ||
          child.topic.trim().length === 0 ||
          hasEmptyChild(child),
      );
    };

    return hasEmptyChild(data.data as MindMapTreeNode | null);
  }

  private flushPendingMindMapSaveCallbacks(): void {
    while (this.pendingMindMapSaveCallbacks.length > 0) {
      this.pendingMindMapSaveCallbacks.shift()?.();
    }
  }

  private scheduleRootFocusRetry(maxAttempts: number = 6): void {
    let attemptsLeft = maxAttempts;
    const runAttempt = () => {
      const focused = this.focusRootForEdit();
      if (focused || attemptsLeft <= 0) {
        return;
      }
      attemptsLeft -= 1;
      setTimeout(runAttempt, 0);
    };

    queueMicrotask(runAttempt);
  }

  private focusRootForEdit(): boolean {
    const service = this.getFlashMapService();
    if (!service) {
      return false;
    }

    let root: ReturnType<FlashMapService['getRoot']>;
    try {
      root = service.getRoot();
    } catch {
      return false;
    }

    if (!root) {
      return false;
    }

    service.selectNode(root);
    service.beginEdit(root);
    this.shouldFocusRoot = false;
    return true;
  }

  private getFlashMapService(): FlashMapService | null {
    return (
      (this.flashMap as unknown as { flashMapService?: FlashMapService })
        ?.flashMapService ?? null
    );
  }

  private async syncPendingNoteDrafts(nodeId: string | null): Promise<void> {
    const nodeIds = nodeId
      ? [nodeId]
      : Array.from(this.pendingNoteDrafts.keys());

    for (const currentNodeId of nodeIds) {
      const draft = this.pendingNoteDrafts.get(currentNodeId);
      if (typeof draft === 'undefined') {
        continue;
      }

      const targetId = this.buildNoteTargetId(currentNodeId);
      if (!targetId) {
        continue;
      }

      const synced = this.hasMeaningfulNoteText(draft)
        ? await this.resolveOrCreateAnnotationLink(
            currentNodeId,
            targetId,
            draft,
          )
        : await this.removeAnnotationLink(currentNodeId, targetId);
      if (synced) {
        this.pendingNoteDrafts.delete(currentNodeId);
      }
    }
  }

  private async resolveOrCreateAnnotationLink(
    nodeId: string,
    targetId: string,
    text: string,
  ): Promise<boolean> {
    try {
      const mindMapId = this.currentMindMap?.id ?? null;
      const workspaceId =
        this.currentMindMap?.workspaceId ?? this.authService.GetWorkspace();
      if (!mindMapId) {
        return false;
      }
      if (!workspaceId) {
        return false;
      }

      const existingLinks = await firstValueFrom(
        this.annotationLinkService.listByTarget({
          mindMapId,
          nodeKey: nodeId,
        }),
      );
      const existingLink = this.pickValidLink(existingLinks);
      if (existingLink) {
        await firstValueFrom(
          this.annotationService.update({
            id: existingLink.annotationId,
            workspaceId,
            text,
          }),
        );
        this.annotationLinkCache.set(targetId, existingLink);
        return true;
      }

      const annotation = await firstValueFrom(
        this.annotationService.create({
          workspaceId,
          text,
        }),
      );
      const annotationId = this.readAnnotationId(annotation);
      if (!annotationId) {
        return false;
      }

      const createdLink = await firstValueFrom(
        this.annotationLinkService.create({
          annotationId,
          mindMapId,
          nodeKey: nodeId,
        }),
      );

      this.annotationLinkCache.set(targetId, {
        annotationId,
        linkId: this.readLinkId(createdLink),
      });
      return true;
    } catch {
      return false;
    }
  }

  private async removeAnnotationLink(
    nodeId: string,
    targetId: string,
  ): Promise<boolean> {
    try {
      const mindMapId = this.currentMindMap?.id ?? null;
      if (!mindMapId) {
        return false;
      }

      let link = this.annotationLinkCache.get(targetId) ?? null;
      if (!link?.linkId) {
        const existingLinks = await firstValueFrom(
          this.annotationLinkService.listByTarget({
            mindMapId,
            nodeKey: nodeId,
          }),
        );
        link = this.pickValidLink(existingLinks);
      }

      if (!link) {
        this.annotationLinkCache.delete(targetId);
        return true;
      }

      if (!link.linkId) {
        return false;
      }

      await firstValueFrom(this.annotationLinkService.delete(link.linkId));
      this.annotationLinkCache.delete(targetId);
      return true;
    } catch {
      return false;
    }
  }

  private pickValidLink(
    links: AnnotationLinkModel[],
  ): AnnotationLinkCacheEntry | null {
    for (const link of links) {
      const annotationId = this.readAnnotationIdFromLink(link);
      if (!annotationId) {
        continue;
      }
      return {
        annotationId,
        linkId: this.readLinkId(link),
      };
    }
    return null;
  }

  private readAnnotationId(model: { id?: number; Id?: number }): number | null {
    const record = model as Record<string, unknown>;
    const id = record['id'] ?? record['Id'] ?? null;
    const parsed = Number(id);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private readAnnotationIdFromLink(model: AnnotationLinkModel): number | null {
    const record = model as Record<string, unknown>;
    const id = record['annotationId'] ?? record['AnnotationId'] ?? null;
    const parsed = Number(id);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private readLinkId(model: { id?: number; Id?: number }): number | null {
    const record = model as Record<string, unknown>;
    const id = record['id'] ?? record['Id'] ?? null;
    const parsed = Number(id);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private hasMeaningfulNoteText(value: string): boolean {
    const withoutTags = value
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\u00a0/g, ' ');

    return withoutTags.trim().length > 0;
  }

  private buildNoteTargetId(nodeId: string): string | null {
    const mindMapId = this.currentMindMap?.id ?? null;
    if (!mindMapId) {
      return null;
    }
    return `mindmap:${mindMapId}:node:${nodeId}`;
  }

  private resetNoteSyncState(): void {
    this.pendingNoteDrafts.clear();
    this.annotationLinkCache.clear();
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString();
  }
}
